import React, { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { useInventoryStore } from '../../../store/inventoryStore';
import { useWarehouseStore } from '../../../store/warehouseStore';
import { useProjectStore } from '../../../store/projectStore';
import { useStockMovementStore, generatePendingId } from '../../../store/stockMovementStore';
import { colours, StepNav, Select, Input, ConditionBadge } from './shared';

const StepIndicator: React.FC<{ steps: string[]; current: number }> = ({ steps, current }) => (
  <div style={{ display: 'flex', alignItems: 'center', width: '100%', padding: '8px 16px 0', boxSizing: 'border-box', flexShrink: 0 }}>
    {steps.map((label, i) => {
      const done = i < current; const active = i === current;
      return (
        <React.Fragment key={i}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <span style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:22, height:22, borderRadius:'50%', fontSize:12, fontWeight:700, backgroundColor: done ? '#16a34a' : active ? '#3b82f6' : '#374151', color:'#fff', flexShrink:0 }}>{done ? '✓' : i + 1}</span>
            <span style={{ fontSize:13, fontWeight:500, whiteSpace:'nowrap', color: active ? colours.text : done ? '#6b7280' : '#4b5563' }}>{label}</span>
          </div>
          {i < steps.length - 1 && <div style={{ flex:1, height:1, margin:'0 16px', backgroundColor: done ? '#16a34a' : colours.border }} />}
        </React.Fragment>
      );
    })}
  </div>
);

interface Props { onQueued: () => void; onOpenReview?: () => void; }

const STEPS = ['Select Units', 'Destination'];

const TransferForm: React.FC<Props> = ({ onQueued, onOpenReview }) => {
  const { appTheme } = useProjectStore();
  const isDark = appTheme === 'dark';
  const { items, stockUnits, fetchItems, fetchStockUnits } = useInventoryStore();
  const { warehouses, storageAreasByWarehouse } = useWarehouseStore();
  const { addPending } = useStockMovementStore();

  const [step, setStep]               = useState(0);
  const [search, setSearch]           = useState('');
  const [filterWarehouse, setFilterWarehouse] = useState('');
  const [filterArea, setFilterArea]   = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [reference, setReference]     = useState('');

  // Destination
  const [toWarehouseId, setToWarehouseId]     = useState('');
  const [toStorageAreaId, setToStorageAreaId] = useState('');

  useEffect(() => { fetchItems(); fetchStockUnits(); }, [fetchItems, fetchStockUnits]);

  const itemById = useMemo(() => new Map(items.map(i => [i.id, i])), [items]);

  const filterAreas = useMemo(() =>
    filterWarehouse ? (storageAreasByWarehouse[filterWarehouse] ?? []) : [],
    [storageAreasByWarehouse, filterWarehouse]
  );

  const toAreas = useMemo(() =>
    toWarehouseId ? (storageAreasByWarehouse[toWarehouseId] ?? []) : [],
    [storageAreasByWarehouse, toWarehouseId]
  );

  // In-stock units only — can't transfer checked-out items
  const inStockUnits = useMemo(() =>
    stockUnits.filter(u => u.status === 'in stock'),
    [stockUnits]
  );

  // Selected unit objects (used to detect same-location destination)
  const selectedUnits = useMemo(() =>
    inStockUnits.filter(u => selectedIds.has(u.id)),
    [inStockUnits, selectedIds]
  );

  // Returns true if picking `toWarehouseId` + `areaId` would be a no-op for ALL selected units
  const isSameLocation = (areaId: string | null) => {
    if (selectedUnits.length === 0) return false;
    return selectedUnits.every(
      u => u.warehouseId === toWarehouseId && (u.storageAreaId ?? null) === (areaId ?? null)
    );
  };

  // Auto-select first non-same-location storage area when destination warehouse changes
  useEffect(() => {
    const current = toAreas.find(a => a.id === toStorageAreaId);
    if (!current || isSameLocation(toStorageAreaId)) {
      const first = toAreas.find(a => !isSameLocation(a.id));
      setToStorageAreaId(first?.id ?? '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toAreas, toWarehouseId]);

  const filtered = useMemo(() => {
    return inStockUnits.filter(u => {
      if (filterWarehouse && u.warehouseId !== filterWarehouse) return false;
      if (filterArea && u.storageAreaId !== filterArea) return false;
      if (search) {
        const q = search.toLowerCase();
        const item = itemById.get(u.itemId);
        return [item?.name, item?.sku, u.serialMac, String(u.unitId)]
          .filter(Boolean).join(' ').toLowerCase().includes(q);
      }
      return true;
    });
  }, [inStockUnits, filterWarehouse, filterArea, search, itemById]);

  const toggle = (id: string) =>
    setSelectedIds(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const toggleAll = () =>
    setSelectedIds(selectedIds.size === filtered.length ? new Set() : new Set(filtered.map(u => u.id)));

  const handleQueue = () => {
    addPending({
      type: 'transfer',
      id: generatePendingId(),
      unitIds: Array.from(selectedIds),
      toWarehouseId,
      toStorageAreaId: toStorageAreaId || null,
      reference,
    });
    setSelectedIds(new Set());
    setSearch('');
    setReference('');
    setStep(0);
    onQueued();
  };

  const { border, cardBg, headerBg, muted, text, inputBg, rowHover } = colours;

  return (
    <div className="flex-1 flex flex-col min-h-0" style={{ backgroundColor: colours.pageBg }}>
      <StepIndicator steps={STEPS} current={step} />

      {/* ── Step 1: Select units ── */}
      {step === 0 && (
        <div className="flex-1 flex flex-col min-h-0 px-4 py-4 gap-3">
          {/* Filters */}
          <div className="flex items-center gap-3 flex-shrink-0 flex-wrap">
            <div className="relative" style={{ minWidth: 260 }}>
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: muted }} />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by name, serial…"
                className="w-full text-sm pl-8 pr-3 py-1.5 rounded border focus:outline-none focus:ring-1 focus:ring-blue-500"
                style={{ backgroundColor: inputBg, borderColor: border, color: text }}
              />
            </div>
            <select
              value={filterWarehouse}
              onChange={e => { setFilterWarehouse(e.target.value); setFilterArea(''); }}
              className="text-sm px-2 py-1.5 rounded border focus:outline-none focus:ring-1 focus:ring-blue-500"
              style={{ backgroundColor: inputBg, borderColor: border, color: text, minWidth: 160 }}
            >
              <option value="">All Warehouses</option>
              {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
            {filterAreas.length > 0 && (
              <select
                value={filterArea}
                onChange={e => setFilterArea(e.target.value)}
                className="text-sm px-2 py-1.5 rounded border focus:outline-none focus:ring-1 focus:ring-blue-500"
                style={{ backgroundColor: inputBg, borderColor: border, color: text, minWidth: 140 }}
              >
                <option value="">All Areas</option>
                {filterAreas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            )}
          </div>

          <div className="flex-1 overflow-auto rounded" style={{ border: `1px solid ${border}` }}>
            <table className="w-full">
              <thead className="sticky top-0" style={{ backgroundColor: headerBg }}>
                <tr style={{ borderBottom: `1px solid ${border}` }}>
                  <th className="px-3 py-2 w-8">
                    <input type="checkbox" checked={selectedIds.size === filtered.length && filtered.length > 0} onChange={toggleAll} />
                  </th>
                  {['Item', 'Unit ID', 'Serial/MAC', 'Warehouse', 'Storage Area', 'Condition'].map(h => (
                    <th key={h} className="px-3 py-2 text-left text-xs uppercase tracking-wide font-medium" style={{ color: muted }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody style={{ backgroundColor: cardBg }}>
                {filtered.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-8 text-center text-sm" style={{ color: muted }}>
                    {inStockUnits.length === 0 ? 'No in-stock units available.' : 'No units match filters.'}
                  </td></tr>
                )}
                {filtered.map((unit, idx) => {
                  const item  = itemById.get(unit.itemId);
                  const wh    = warehouses.find(w => w.id === unit.warehouseId);
                  const areas = storageAreasByWarehouse[unit.warehouseId] ?? [];
                  const area  = areas.find(a => a.id === unit.storageAreaId);
                  const sel   = selectedIds.has(unit.id);
                  return (
                    <tr
                      key={unit.id}
                      className="cursor-pointer transition-colors"
                      style={{
                        borderBottom: idx < filtered.length - 1 ? `1px solid ${border}` : undefined,
                        backgroundColor: sel ? '#1d2535' : undefined,
                      }}
                      onClick={() => toggle(unit.id)}
                      onMouseEnter={e => { if (!sel) (e.currentTarget as HTMLElement).style.backgroundColor = rowHover; }}
                      onMouseLeave={e => { if (!sel) (e.currentTarget as HTMLElement).style.backgroundColor = ''; }}
                    >
                      <td className="px-3 py-1.5"><input type="checkbox" checked={sel} onChange={() => toggle(unit.id)} onClick={e => e.stopPropagation()} /></td>
                      <td className="px-3 py-1.5 text-sm font-medium" style={{ color: text }}>{item?.name ?? '—'}</td>
                      <td className="px-3 py-1.5">
                        <span className="inline-flex items-center justify-center text-xs font-medium rounded px-1.5 py-0.5" style={{ backgroundColor: '#374151', color: '#d1d5db' }}>
                          {unit.unitId}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-sm" style={{ color: unit.serialMac ? text : muted }}>{unit.serialMac || '—'}</td>
                      <td className="px-3 py-1.5 text-sm" style={{ color: text }}>{wh?.name ?? '—'}</td>
                      <td className="px-3 py-1.5 text-sm" style={{ color: area ? text : muted }}>{area?.name ?? '—'}</td>
                      <td className="px-3 py-1.5"><ConditionBadge condition={unit.condition} isDark={isDark} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {selectedIds.size > 0 && (
            <p className="text-sm flex-shrink-0" style={{ color: muted }}>{selectedIds.size} unit{selectedIds.size > 1 ? 's' : ''} selected</p>
          )}
        </div>
      )}

      {/* ── Step 2: Destination ── */}
      {step === 1 && (
        <div className="flex-1 overflow-auto px-4 py-4">
          <div
            className="max-w-md rounded p-5 flex flex-col gap-4"
            style={{ backgroundColor: cardBg, border: `1px solid ${border}` }}
          >
            <p className="text-sm" style={{ color: muted }}>
              Move {selectedIds.size} unit{selectedIds.size > 1 ? 's' : ''} to:
            </p>
            <Select label="Destination Warehouse" value={toWarehouseId} onChange={e => { setToWarehouseId(e.target.value); setToStorageAreaId(''); }} wFull>
              <option value="">Select warehouse…</option>
              {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
            </Select>
            <Select label="Storage Area" value={toStorageAreaId} onChange={e => setToStorageAreaId(e.target.value)} wFull disabled={toAreas.length === 0}>
              {toAreas.length === 0
                ? <option value="">No storage areas</option>
                : toAreas.map(a => {
                    const same = isSameLocation(a.id);
                    return (
                      <option key={a.id} value={a.id} disabled={same}>
                        {a.name}{same ? ' (current location)' : ''}
                      </option>
                    );
                  })
              }
            </Select>
            <Input label="Reference (optional)" placeholder="Transfer note…" value={reference} onChange={e => setReference(e.target.value)} wFull />
          </div>
        </div>
      )}

      <StepNav
        onBack={step > 0 ? () => setStep(0) : undefined}
        onNext={step === 0 ? () => setStep(1) : undefined}
        onQueue={step === 1 ? handleQueue : undefined}
        onReview={onOpenReview}
        nextDisabled={selectedIds.size === 0}
        queueDisabled={!toWarehouseId || isSameLocation(toStorageAreaId || null)}
        queueLabel="Add to Queue ✓"
      />
    </div>
  );
};

export default TransferForm;
